/* eslint-disable max-len */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  Button,
} from '@one-thd/sui-atomic-components';
import { useDraggableDrawer } from '../../hooks/useDraggableDrawer';
import { subscribeAssistantReady, hasAnnouncedAssistantReady } from '../../platform/events/lifecycle';
import { DRAWER_HEIGHTS } from '../../shared/constants';

/**
 * A draggable drawer.
 *
 * @param {object} props - The properties for the DraggableDrawer component.
 * - `children`: Content to display inside the drawer.
 * - `drawerState`: The state of the mobile drawer.
 * - `setDrawerState`: Function to update drawerState.
 *
 * @returns {JSX.Element} The DraggableDrawer component.
 */

const DraggableDrawer = ({
  children,
  drawerState,
  setDrawerState,
}) => {
  const [currentHeight, setCurrentHeight] = useState(DRAWER_HEIGHTS[drawerState]);
  const {
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    dynamicHeight,
    dynamicBottom,
  } = useDraggableDrawer(drawerState, setDrawerState, currentHeight);
  const triggerRef = useRef(null);
  // Seeded from the latch, not just the subscription — this drawer mounts lazily, after
  // readiness has typically already announced. See .claude/context/event-bus.md.
  const [isAssistantReady, setIsAssistantReady] = useState(hasAnnouncedAssistantReady);

  useEffect(() => {
    // Readiness is monotonic, so this only ever latches true — see the contract in
    // platform/events/lifecycle.js, which also owns the frame/channel this resolves against.
    return subscribeAssistantReady((isReady) => {
      if (isReady) setIsAssistantReady(true);
    });
  }, []);

  const handleBarLabel = drawerState === 'OPEN' ? 'Drag down to minimize' : 'Drag to open or close';

  const containerClasses = classNames(
    'drawer-container sui-flex sui-flex-col sui-overflow-y-hidden sui-bg-primary sui-z-max sui-outline-none sui-max-w-full sui-w-full sui-rounded-t-lg sui-bottom-0 sui-left-0 sui-transition-[height,bottom] sui-duration-500',
    drawerState === 'OPEN' ? 'sui-fixed' : 'sui-absolute',
    {
      'sui-shadow-[0_-1px_6px_2px_rgba(0,0,0,0.3)]': drawerState !== 'CLOSED',
      'sui-hidden': !isAssistantReady && drawerState === 'MINIMIZED'
    }
  );

  useEffect(() => {
    setCurrentHeight(DRAWER_HEIGHTS[drawerState]);
  }, [drawerState]);

  useEffect(() => {
    const handleInteraction = (event) => {
      if (triggerRef.current && triggerRef.current.contains(event.target)) {
        event.preventDefault();
      }
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('touchstart', handleInteraction, { passive: false });
    }

    return (() => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('touchstart', handleInteraction);
      }
    });
  }, []);

  return (
    <>
      {/* Keyboard-gap filler; see .claude/context/drawer-layout.md's DrawerFrame section. */}
      {drawerState === 'OPEN' && (
        <div
          className="sui-fixed sui-inset-x-0 sui-bottom-0 sui-bg-primary"
          style={{ height: dynamicBottom }}
        />
      )}
      <div
        className={containerClasses}
        style={{ height: dynamicHeight, bottom: dynamicBottom }}
        data-component="DraggableDrawer"
      >
        <Button
          className="drawer-header-with-handlebar-div sui-bg-primary sui-sticky sui-top-0 sui-bottom-0 sui-z-200 sui-border-none"
          onTouchStart={handlePointerDown}
          onMouseDown={handlePointerDown}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
          onMouseUp={handlePointerUp}
          ref={triggerRef}
          aria-label={handleBarLabel}
          role="button"
        >
          <div
            className="sui-w-16 sui-h-1 sui-bg-strong sui-rounded-full sui-mt-2 sui-mb-4 sui-block sui-mx-auto"
          >
            <span className="sui-sr-only">{handleBarLabel}</span>
          </div>
        </Button>
        {children}
      </div>
    </>
  );
};

DraggableDrawer.displayName = 'DraggableDrawer';

DraggableDrawer.propTypes = {
  children: PropTypes.node.isRequired,
  drawerState: PropTypes.oneOf(['OPEN', 'MINIMIZED', 'CLOSED']).isRequired,
  setDrawerState: PropTypes.func.isRequired,
};

export { DraggableDrawer };
